import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import {
  nestPortfolioDataPoints,
  sortPortfolioDataPoints,
} from '@products/portfolio/portfolio.utils';
import {
  PortfolioComponentId,
  PortfolioCurrencyAllocationDTO,
  PortfolioDTO,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { getFirstNonEmptyProp, parseStdNumber, parseYN } from '../mapper.utils';
import { Mapper, MapperParams } from '../type.mapper';
import { Logger } from '@utils/logger';

const logger = Logger.getLogger('CurrencyAllocationMapper');

export const isCurrencyExposure = (
  point: PortfolioCurrencyAllocationDTO
): boolean => !isCurrencyBreakdown(point);

export const isCurrencyBreakdown = (
  point: PortfolioCurrencyAllocationDTO
): boolean => point.calcnamestd === 'CRNCYXHEDGE_WGT';

const mapCurrencyAllocation = (
  data: PortfolioCurrencyAllocationDTO[]
): PortfolioAllocation => {
  let componentId: PortfolioComponentId;
  let baseLabelKey: string;
  switch (data[0].calcnamestd) {
    case 'CRNCYXHEDGE_WGT':
      componentId = PortfolioComponentId.CURRENCY_BREAKDOWN;
      baseLabelKey = 'currency-breakdown';
      break;
    default:
      componentId = PortfolioComponentId.CURRENCY_EXPOSURE;
      baseLabelKey = 'currency-exposure';
      break;
  }

  let dataPoints: PortfolioAllocationDataPoint[] = data
    .sort(sortPortfolioDataPoints)
    .map(
      (obj: PortfolioCurrencyAllocationDTO): PortfolioAllocationDataPoint => ({
        label: obj.brkdwnname,
        parent: obj.parent,
        benchmark: formatPercentage(obj.bmbrkdwnpct),
        benchmarkStd: parseStdNumber(obj.bmbrkdwnpctstd),
        breakdown: formatPercentage(obj.brkdwnpct),
        breakdownStd: parseStdNumber(obj.brkdwnpctstd),
      })
    );

  dataPoints = nestPortfolioDataPoints(dataPoints);

  return {
    componentId,
    caveatComponentId: PortfolioComponentId.CURRENCY_BREAKDOWN,
    baseLabelKey,
    allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
    allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
    asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
    calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
    calculationType: getFirstNonEmptyProp(data, 'calcname'),
    hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
    updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
    benchmarkName: getFirstNonEmptyProp(data, 'bmname'),
    data: dataPoints,
  };
};

// tslint:disable:max-line-length
/**
 * example funds
 * (normal data): https://staging.ftsites.com/en-lu/our-funds/price-and-performance/products/4817/Z/franklin-european-total-return-fund/LU0170473374#currency-breakdown
 * (nested data): https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/97/A/templeton-global-bond-fund/TPINX#currency-breakdown
 * (breakdown/exposure split): https://en-us.staging.ftsites.com/investments/options/closed-end-funds/products/90884/SINGLCLASS/brandywine-global-global-income-opportunities-fund-inc/BWG#currency-breakdown
 */
// tslint:enable
export class CurrencyAllocationMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  public toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioCurrencyAllocationDTO[] =
      portfolioDto.currencyallocation;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const allocations: PortfolioAllocation[] = [];

    const currencyExposureData: PortfolioCurrencyAllocationDTO[] = data.filter(
      isCurrencyExposure
    );
    if (currencyExposureData.length > 0) {
      allocations.push(mapCurrencyAllocation(currencyExposureData));
    }

    const currencyBreakdownData: PortfolioCurrencyAllocationDTO[] = data.filter(
      isCurrencyBreakdown
    );
    if (currencyBreakdownData.length > 0) {
      allocations.push(mapCurrencyAllocation(currencyBreakdownData));
    }

    logger.debug('allocations', allocations);

    return allocations;
  }
}
